import { useMemo } from "react";

import { Holiday } from "../types";
import {
	getDateKeyInTimeZone,
	getHolidaysOnDate,
	getRepeatOccurrenceDateKeys,
} from "../utils/holiday-dates";
import useLocationHolidays from "./use-location-holidays";

export interface RepeatHolidayValues {
	startTime: string;
	locationId: number | string;
	repeatOn: Record<string, boolean>;
	endType: "OnDate" | "AfterOccurrences";
	repeatEndDate: string;
	occurrences: number | null;
	skipHolidayDates?: boolean;
}

export interface RepeatHolidayPreview {
	// Number of repeats the pattern generates
	total: number;
	// The repeats that land on a holiday at the Location
	holidayRepeats: { dateKey: string; holidays: Holiday[] }[];
	// Skipping is on and every repeat is a holiday: nothing would be created
	isNothingToCreate: boolean;
}

/**
 * Which repeats of a Repeat Shift pattern land on holidays at the Location,
 * using the Location's dates. A preview only — the Repeat Shift response's
 * `skippedHolidayDates` is what was actually excluded.
 */
const useRepeatHolidayPreview = (
	values: RepeatHolidayValues
): RepeatHolidayPreview | null => {
	const locationId = Number(values.locationId) || 0;
	const { getLocationTimeZone, getHolidaysForLocation } = useLocationHolidays(
		locationId ? [locationId] : []
	);

	return useMemo(() => {
		const start = new Date(values.startTime);
		if (!locationId || Number.isNaN(start.getTime())) return null;
		const daysOfWeek = Object.entries(values.repeatOn ?? {})
			.filter(([, isSelected]) => isSelected)
			.map(([dayIndex]) => Number(dayIndex));
		const repeatDates = getRepeatOccurrenceDateKeys({
			startKey: getDateKeyInTimeZone(start, getLocationTimeZone(locationId)),
			daysOfWeek,
			endKey: values.endType === "OnDate" ? values.repeatEndDate : null,
			occurrences:
				values.endType === "AfterOccurrences" ? values.occurrences : null,
		});
		const holidays = getHolidaysForLocation(locationId);
		const holidayRepeats = repeatDates
			.map((dateKey) => ({ dateKey, holidays: getHolidaysOnDate(holidays, dateKey) }))
			.filter((repeat) => repeat.holidays.length > 0);
		return {
			total: repeatDates.length,
			holidayRepeats,
			isNothingToCreate:
				!!values.skipHolidayDates &&
				repeatDates.length > 0 &&
				holidayRepeats.length === repeatDates.length,
		};
	}, [
		values.startTime,
		values.repeatOn,
		values.endType,
		values.repeatEndDate,
		values.occurrences,
		values.skipHolidayDates,
		locationId,
		getLocationTimeZone,
		getHolidaysForLocation,
	]);
};

export default useRepeatHolidayPreview;
